import { createFileRoute, Link } from "@tanstack/react-router";
import { getReleases } from "@/lib/music.functions";
import type { Release } from "@/lib/content";
import { formatReleaseDate } from "@/lib/content";
import { SITE_URL, absoluteUrl } from "@/lib/site";
import { breadcrumbs, discographyGraph, ldScript, seoMeta } from "@/lib/schema";

const TITLE = "Discography | Buddha KA Complete Music Catalog";
const DESCRIPTION =
  "The complete Buddha KA discography on KAfrequency — every album, EP, and single with release dates, tuning frequency, and full track listings.";

export const Route = createFileRoute("/music/discography")({
  loader: async () => ({ releases: await getReleases() }),
  component: Discography,
  head: ({ loaderData }) => {
    const releases = loaderData?.releases ?? [];
    return {
      ...seoMeta({ title: TITLE, description: DESCRIPTION, path: "/music/discography" }),
      links: [{ rel: "canonical", href: absoluteUrl("/music/discography") }],
      scripts: [
        ...(releases.length ? [ldScript(discographyGraph(releases as never))] : []),
        ldScript(breadcrumbs([["Music", "/music"], ["Discography", "/music/discography"]])),
      ],
    };
  },
});

function Discography() {
  const { releases } = Route.useLoaderData() as { releases: Release[] };
  const groups = [
    { label: "Albums & EPs", items: releases.filter((r) => r.type !== "Single") },
    { label: "Singles", items: releases.filter((r) => r.type === "Single") },
  ];

  return (
    <section className="mx-auto max-w-5xl px-4 py-20 sm:px-6">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Canonical Catalog</p>
      <h1 className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl">Discography</h1>
      <p className="mt-4 max-w-2xl text-muted-foreground">{DESCRIPTION}</p>
      <p className="mt-6 text-sm">
        <Link to="/artist/buddha-ka" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous">
          Official Buddha KA artist page
        </Link>{" "}
        <span className="text-muted-foreground">— verified streaming profiles and artist identity.</span>
      </p>
      <p className="mt-3 text-sm">
        <a href={`${SITE_URL}/music/catalog.json`} className="text-gold hover:text-gold-luminous">
          Machine-readable catalog (JSON-LD)
        </a>
      </p>

      {groups.map((g) =>
        g.items.length ? (
          <section key={g.label} className="mt-14" aria-labelledby={g.label}>
            <h2 id={g.label} className="font-serif text-2xl text-gold-luminous">{g.label}</h2>
            <ul className="mt-6 divide-y divide-border/60">
              {g.items.map((r) => (
                <li key={r.slug} className="py-5">
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <Link
                      to="/music/$slug"
                      params={{ slug: r.slug }}
                      className="font-serif text-xl text-ivory hover:text-gold-luminous"
                    >
                      {r.title}
                    </Link>
                    <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                      {[r.type, formatReleaseDate(r) ?? r.year, r.frequency].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                  {Object.keys(r.links ?? {}).length > 0 ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {Object.entries(r.links ?? {}).map(([platform, url]) => (
                        <a
                          key={platform}
                          href={url as string}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-full border border-border/60 px-3 py-1 text-[11px] text-muted-foreground hover:border-gold hover:text-gold"
                        >
                          Listen on {platform}
                        </a>
                      ))}
                    </div>
                  ) : null}
                  {r.tracks?.length ? (
                    <ol className="mt-3 grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
                      {r.tracks.map((t, i) => (
                        <li key={`${r.slug}-${i}`}>
                          <span className="text-gold/70">{String(i + 1).padStart(2, "0")}</span> {t.title}
                          {t.duration ? <span className="text-muted-foreground/70"> · {t.duration}</span> : null}
                        </li>
                      ))}
                    </ol>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        ) : null,
      )}

      {releases.length === 0 && <p className="mt-16 text-muted-foreground">The catalog is temporarily unavailable.</p>}
    </section>
  );
}
